/**
 * Each control for one firm: March 2025, CDW 2026 and the current status (reviewed wins), with risk and
 * wave tags and the review state. Filters by area (linked from the dashboard's areas table), priority,
 * BTI wave and current status. Those filters stay in the address when the view changes.
 */
import { areaName, CDW_GAP_STATUS, resultLabel, RISK_IMPACT, RISK_LABEL, RISK_ORDER, SURVEY_STATUS } from '@grc/shared/vocabulary';
import { useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useApi } from '../api';
import { Load, PageHeader, RiskChip, Status, WaveTag } from '../components';
import { ExplainedPanel, WithPanel } from '../Explained';
import { ViewTabs } from '../tabs';
import type { FirmControls as Data, FirmControlRow } from '../types';
import { NetworkBanner } from './FirmOverview';
import { catalogMatch, cleanSearch, readCatalog, writeCatalog, Worklist, type PlaceFilter, type WaveFilter, type Catalog } from './SelfAssessment';

const SOURCE: Record<string, string> = { cdw: 'CDW 2026', reviewed: 'Reviewed by National IT', self: "The firm's own rating" };

export function CurrentChip({ c }: { c: Pick<FirmControlRow, 'currentStatus' | 'currentSource'> }) {
  if (!c.currentStatus) return <span className="muted">Not rated</span>;
  const label = resultLabel(c.currentStatus);
  const tone = c.currentSource === 'reviewed' ? 'reviewed' : CDW_GAP_STATUS[c.currentStatus]?.tone ?? 'neutral';
  return (
    <>
      <span className={`chip tone-${tone}`}>{label}</span>
      <div className="muted small">{SOURCE[c.currentSource ?? 'cdw']}</div>
    </>
  );
}

/**
 * Two views of the same controls (DEC-31, corrected 2026-10-04): the self-assessment worklist, which the firm's IT
 * leads open on, and March 2025 beside 2026, which everyone else opens on. The view is in the address (?view=);
 * a link with ?area= (the dashboard's areas table) opens the comparison.
 */
type View = 'worklist' | 'compare';
const VIEWS: Array<{ id: View; label: string }> = [
  { id: 'worklist', label: 'Self-assessment' },
  { id: 'compare', label: 'March 2025 and 2026' },
];

export function FirmControls() {
  const { firmCode } = useParams();
  const state = useApi<Data>(`/firms/${firmCode}/controls`);
  return (
    <Load state={state} what="controls">
      {(d) => <ControlsPage d={d} />}
    </Load>
  );
}

function ControlsPage({ d }: { d: Data }) {
  const [params, setParams] = useSearchParams();
  const areas = useMemo(() => [...new Set(d.controls.map((c) => c.domainNumber))], [d.controls]);
  const query = params.toString();
  useEffect(() => {
    const cleaned = cleanSearch(new URLSearchParams(window.location.search), areas);
    if (cleaned) setParams(cleaned, { replace: true });
  }, [query, areas, setParams]);
  const asked = params.get('view');
  // From the live address, so a quick second change never works from an older query (as on Leadership).
  // Shared filters (wave, place, area, priority, search) stay; only the view changes.
  const show = (v: View) => {
    const next = new URLSearchParams(window.location.search);
    if (next.get('view') === v) return;
    next.set('view', v);
    setParams(next);
  };
  const view: View = asked === 'worklist' || asked === 'compare' ? asked : params.get('area') || !d.canUpdate ? 'compare' : 'worklist';
  return (
    <>
      <NetworkBanner viewer={d.viewer} firmName={d.firmCode} />
      <p className="page-back">
        <Link to={`/firm/${d.firmCode}`}>Back to the dashboard</Link>
      </p>
      <PageHeader title="Each control" />
      {/* CP-10: keys move from the tab the live address names, as on the other tabbed pages. */}
      <ViewTabs
        views={VIEWS}
        view={view}
        onSelect={show}
        liveView={() => {
          const live = new URLSearchParams(window.location.search).get('view');
          return live === 'worklist' || live === 'compare' ? live : view;
        }}
        label="Views of the controls"
        idPrefix="controls"
      />
      <div id="controls-panel" role="tabpanel" aria-labelledby={`controls-tab-${view}`} tabIndex={0} className="lead-panel">
        {view === 'worklist' ? (
          <WithPanel panel={<ExplainedPanel show={['worklist', 'risk']} />}>
            <Worklist d={d} />
          </WithPanel>
        ) : (
          <Compare d={d} areas={areas} />
        )}
      </div>
    </>
  );
}

function Compare({ d, areas }: { d: Data; areas: string[] }) {
  const [params, setParams] = useSearchParams();
  const catalog = readCatalog(params, areas);
  const set = (patch: Partial<Catalog>) => setParams(writeCatalog(new URLSearchParams(window.location.search), patch));
  return (
    <WithPanel panel={<ExplainedPanel show={['status', 'risk', 'sources']} />}>
      <p className="lede">March 2025 beside 2026 and the current status, control by control. Open a control to see its detail{d.canUpdate ? ' or rate it with the assistant' : ''}.</p>
      <div className="filters">
        <label>
          Area
          <select value={catalog.area} onChange={(e) => set({ area: e.target.value })}>
            <option value="all">All areas</option>
            {areas.map((a) => (
              <option key={a} value={a}>
                {areaName(a)}
              </option>
            ))}
          </select>
        </label>
        <label>
          Priority
          <select value={catalog.priority} onChange={(e) => set({ priority: e.target.value as Catalog['priority'] })}>
            <option value="all">All</option>
            {(['critical', 'high', 'medium'] as const).map((l) => (
              <option key={l} value={l}>
                {RISK_LABEL[l]}
              </option>
            ))}
          </select>
        </label>
        <label>
          BTI wave
          <select value={catalog.wave} onChange={(e) => set({ wave: e.target.value as WaveFilter })}>
            <option value="all">All controls</option>
            <option value="1">Wave 1</option>
            <option value="2">Wave 2</option>
            <option value="none">Not in a wave</option>
          </select>
        </label>
        <label>
          Current status
          <select value={catalog.place} onChange={(e) => set({ place: e.target.value as PlaceFilter })}>
            <option value="all">All</option>
            <option value="full">Fully in place</option>
            <option value="partial">Partly in place</option>
            <option value="gap">Not yet in place</option>
            <option value="none">Not rated</option>
          </select>
        </label>
      </div>
      <Table d={d} catalog={catalog} />
    </WithPanel>
  );
}

function Table({ d, catalog }: { d: Data; catalog: Catalog }) {
  const rows = useMemo(
    () =>
      d.controls
        .filter((c) => catalogMatch(c, { ...catalog, q: '' }))
        .sort((a, b) => (catalog.area === 'all' ? 0 : RISK_ORDER[RISK_IMPACT[a.controlId]!.level] - RISK_ORDER[RISK_IMPACT[b.controlId]!.level])),
    [d.controls, catalog],
  );
  return (
    <div className="table-wrap" tabIndex={0} role="region" aria-label="Controls">
      <table>
        <caption className="sr-only">Controls for the firm, {rows.length} shown</caption>
        <thead>
          <tr>
            <th scope="col">Control</th>
            <th scope="col">
              March 2025 <span className="th-note">{d.measures.surveyStatus}</span>
            </th>
            <th scope="col">
              CDW 2026 <span className="th-note">{d.measures.cdwStatus}</span>
            </th>
            <th scope="col">
              Current <span className="th-note">{d.measures.currentStatus}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.controlId}>
              <td>
                <Link to={`/firm/${d.firmCode}/controls/${c.controlId}`}>
                  <span className="cid">{c.framework === 'CIS' ? c.controlId : 'E8'}</span> {c.title}
                </Link>
                <div className="tags">
                  <RiskChip controlId={c.controlId} />
                  <WaveTag wave={c.btiWave} />
                  {c.reviewState === 'submitted' && <span className="chip tone-neutral">Awaiting review</span>}
                  {c.reviewState === 'returned' && <span className="chip tone-attention">Returned with questions</span>}
                  {c.openPlans && <span className="muted small">Plan {c.openPlans}</span>}
                </div>
              </td>
              <td>{c.surveyStatus ? <Status map={SURVEY_STATUS} value={c.surveyStatus} /> : <span className="muted">Not surveyed</span>}</td>
              <td>{c.cdwStatus ? <Status map={CDW_GAP_STATUS} value={c.cdwStatus} /> : <span className="muted">Not assessed</span>}</td>
              <td>
                <CurrentChip c={c} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
